import React, { useState, useRef } from 'react';
import { MapPin, RefreshCw } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useMetarContext } from '../contexts/MetarContext';
import MetarDetails from './MetarDetails';
import { useLanguage } from '../contexts/LanguageContext';

const getCategoryColor = (category?: string) => {
  switch (category) {
    case 'VFR':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'MVFR':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'IFR':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'LIFR':
      return 'bg-pink-100 text-pink-800 border-pink-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const MetarDisplay: React.FC = () => {
  const { t } = useLanguage();
  const { organization, loading: orgLoading } = useOrganization();
  const { metarData, loading, error, refresh } = useMetarContext();
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Hide popover on click outside
  React.useEffect(() => {
    if (!showPopover) return;
    function handle(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowPopover(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showPopover]);

  if (orgLoading || !organization?.baseAirport?.icaoCode) return null;

  return (
    <div className="relative">
      <div
        className="flex flex-nowrap items-center w-auto max-w-md bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 space-x-3 cursor-pointer"
        onMouseEnter={() => setShowPopover(true)}
        onMouseLeave={() => setShowPopover(false)}
        onFocus={() => setShowPopover(true)}
        onBlur={() => setShowPopover(false)}
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={showPopover}
      >
        {/* Flight Category Badge */}
        {loading ? (
          <div className="h-6 w-12 bg-gray-200 rounded animate-pulse flex-shrink-0"></div>
        ) : (
          <span
            className={`border text-xs font-bold rounded px-2 py-0.5 uppercase flex-shrink-0 ${getCategoryColor(metarData?.flight_category)}`}
            title={metarData?.flight_category || t('common.unknown')}
            style={{ minWidth: 40, textAlign: 'center' }}
          >
            {metarData?.flight_category || '--'}
          </span>
        )}
        {/* ICAO Code */}
        <div className="flex items-center space-x-1 flex-shrink-0">
          <MapPin className="h-3 w-3 text-gray-500" />
          <span className="text-xs text-gray-700 font-medium">
            {organization.baseAirport.icaoCode}
          </span>
        </div>
        {/* Raw METAR Message */}
        <div className="text-xs text-gray-800 font-mono overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
          {loading ? (
            <span className="flex items-center"><RefreshCw className="h-3 w-3 animate-spin mr-1" />{t('common.loading')}</span>
          ) : error ? (
            <span className="text-red-600">{t('common.error')}: {error}</span>
          ) : metarData ? (
            metarData.raw || <span className="text-red-600">{t('metar.noRawMetarFound')}</span>
          ) : (
            <span>{t('metar.noData')}</span>
          )}
        </div>
        {/* Refresh Button */}
        <button
          onClick={refresh}
          className="text-blue-600 hover:text-blue-800 transition-colors ml-2 flex-shrink-0"
          title={t('metar.refresh')}
          type="button"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      {showPopover && (
        <div
          ref={popoverRef}
          className="absolute left-0 z-50 mt-2 w-[420px] max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg p-4"
          style={{ minWidth: 320 }}
        >
          <MetarDetails
            metarData={metarData}
            loading={loading}
            error={error}
            icao={organization.baseAirport.icaoCode}
          />
        </div>
      )}
    </div>
  );
};

export default MetarDisplay; 